import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";
import { DeleteGuardResult } from "../../types/deleteGuard";

export interface AlunoType {
    id: number;
    referenceCode?: number;
    name: string;
    email: string;
    cpf?: string;
    address?: string;
    addressNumber?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    landline?: string;
    telephone_01?: string;
    telephone_02?: string;
    telephone_03?: string;
    telephone_04?: string;
    telephone_05?: string;
    telephone_06?: string;
    facebook?: string;
    instagram?: string;
    keepThePhotos?: boolean;
    hasAlbum?: boolean;
    numberOfPhotos?: number;
    cursoId: number;
    cursoName: string;
    base64Image?: string;
    observacao?: string;
}

export interface CreateAlunoType {
    referenceCode?: number;
    name: string;
    email: string;
    cpf?: string;
    address?: string;
    addressNumber?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    landline?: string;
    telephone_01?: string;
    telephone_02?: string;
    telephone_03?: string;
    telephone_04?: string;
    telephone_05?: string;
    telephone_06?: string;
    facebook?: string;
    instagram?: string;
    keepThePhotos?: boolean;
    hasAlbum?: boolean;
    numberOfPhotos?: number;
    password: string;
    contratoId: number;
    cursoId: number;
    base64Image?: string;
    observacao?: string;
}

export interface EditAlunoType {
    id: number;
    referenceCode?: number;
    name: string;
    email: string;
    cpf?: string;
    address?: string;
    addressNumber?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    landline?: string;
    telephone_01?: string;
    telephone_02?: string;
    telephone_03?: string;
    telephone_04?: string;
    telephone_05?: string;
    telephone_06?: string;
    facebook?: string;
    instagram?: string;
    keepThePhotos?: boolean;
    hasAlbum?: boolean;
    numberOfPhotos?: number;
    password?: string;
    base64Image?: string;
    observacao?: string;
}

export interface VendaFormaPagamentoDetalheType {
    formaPagamentoId: number;
    formaPagamentoNome: string;
    valor: number;
    numeroParcelas?: number;
    intervaloDias?: number;
    vencimentoPrimeiraParcela?: string;
}

export interface VendaBoletoDetalheType {
    id: number;
    parcela: number;
    totalParcelas: number;
    valor: number;
    vencimento?: string;
    nossoNumero?: string;
    linhaDigitavel?: string;
    codigoBarras?: string;
    status?: string;
    dataBaixa?: string;
}

export interface VendaDetalhadaAlunoType {
    vendaId: number;
    valorNegociado: number;
    dataVenda: string;
    produtoNome?: string;
    vendedorNome?: string;
    clienteNome?: string;
    clienteCpf?: string;
    formasPagamento: VendaFormaPagamentoDetalheType[];
    boletos: VendaBoletoDetalheType[];
}

export interface AlunoDetalhadoType {
    referenceCode?: number;
    alunoId: number;
    alunoNome: string;
    email?: string;
    cpf?: string;
    cursoNome?: string;
    cursoId?: number;
    contratoId?: number;
    relatorioId?: number;
    albumId?: number;
    vendedorId?: number;
    vendedorNome?: string;
    instituicaoNome?: string;
    cidade?: string;
    numeroFotos?: number;
    status?: number;
    justificativaRecusa?: string;
    base64Image?: string;
    observacao?: string;
    vendaId?: number;
    vendaValor?: number;
    vendaProdutoNome?: string;
    vendaData?: string;
}

export interface AlunoRelatorioRequest {
    filters: {
        pageNumber: number;
        pageSize: number;
        name?: string;
        alunoId?: number;
        contratoId?: number;
        empresaId?: number;
        instituicaoId?: number;
        cursoId?: number;
        cidade?: string;
        state?: string;
        region?: string;
        relatorioId?: number;
        albumId?: number;
        dataInicio?: string;
        dataFim?: string;
    };
    camposSelecionados: string[];
}

export type CopyTransferMode = "copy" | "transfer";

export interface AlunoCursoItem {
    alunoId: number;
    cursoId: number;
}

export interface CopyTransferAlunosRequest {
    sourceContratoId: number;
    targetContratoId: number;
    alunos: AlunoCursoItem[];
    targetCursoId?: number;
    mode?: CopyTransferMode;
}

export interface CopyTransferAlunosResponse {
    total: number;
    copied: number;
    transferred: number;
    skippedAlreadyLinked: number;
    notFound: number;
    missingCursos: string[];
    errors: string[];
}

export interface RemoveAlunoFromContratoResponse {
    message?: string;
    contratoId: number;
    cursoId: number;
    alunoId: number;
}

export default function useAluno() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout } = useContext(AuthContext);

    const [listAluno, setListAluno] = useState<AlunoType[]>([]);
    const [alunoPageCount, setAlunoPageCount] = useState<number>(0);
    const [totalAlunos, setTotalAlunos] = useState<number>(0);
    const [initialListAluno, setInitialListAluno] = useState<AlunoType[]>([]);
    const [listAlunoDetalhado, setListAlunoDetalhado] = useState<AlunoDetalhadoType[]>([]);
    const [alunoDetalhadoPageCount, setAlunoDetalhadoPageCount] = useState<number>(0);

    const [aluno, setAluno] = useState<AlunoType>({
        id: 0,
        referenceCode: 0,
        name: "",
        email: "",
        cpf: "",
        address: "",
        addressNumber: "",
        neighborhood: "",
        city: "",
        state: "",
        zipCode: "",
        landline: "",
        telephone_01: "",
        telephone_02: "",
        telephone_03: "",
        telephone_04: "",
        telephone_05: "",
        telephone_06: "",
        facebook: "",
        instagram: "",
        keepThePhotos: false,
        hasAlbum: false,
        numberOfPhotos: 0,
        cursoId: 0,
        cursoName: "",
        base64Image: "",
    });

    function createAluno(createAlunoType: CreateAlunoType): Promise<CreateAlunoType | null> {
        setLoading(true);
        return api
            .post(`/Aluno/create`, createAlunoType)
            .then((response) => {
                const returnCreatedAlunoId = response.data;
                showToast("success", "Aluno criado com sucesso.");
                setLoading(false);
                return returnCreatedAlunoId;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    const data = error?.response?.data;
                    const backendMessage =
                        typeof data === "string" ? data : data?.detail || data?.title || data?.message;
                    showToast(
                        "error",
                        backendMessage || "Erro ao criar aluno. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar aluno:", error);
                return null;
            });
    }

    function getAlunoPaged(
        pageNumber: number,
        contratoId: number,
        pageSize?: number,
        name?: string,
        alunoId?: number,
        cursoId?: number,
        referenceCode?: number,
        empresaId?: number
    ): Promise<AlunoType[] | null> {
        setLoading(true);
        let url = `/Aluno/getAlunoPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (contratoId) url += `&ContratoId=${contratoId}`;
        if (name) url += `&Name=${name}`;
        if (alunoId) url += `&AlunoId=${alunoId}`;
        if (cursoId) url += `&CursoId=${cursoId}`;
        if (referenceCode) url += `&ReferenceCode=${referenceCode}`;
        if (empresaId) url += `&EmpresaId=${empresaId}`;

        return api
            .get(url)
            .then((response) => {
                setListAluno(response.data.result);
                setInitialListAluno(response.data.result);
                setAlunoPageCount(response.data.pageCount);
                setTotalAlunos(response.data.totalAlunos || 0);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    showToast("error", "Erro de autenticação ao buscar alunos. Atualize a página ou faça login novamente.");
                } else {
                    showToast("error", "Erro ao carregar alunos. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao carregar alunos:", error);
                return null;
            });
    }

    function updateAluno(updateAlunoData: EditAlunoType): Promise<EditAlunoType | null> {
        setLoading(true);
        return api
            .put(`/Aluno/${updateAlunoData.id}`, updateAlunoData)
            .then((response) => {
                showToast("success", "Aluno atualizado com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao atualizar aluno. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao atualizar aluno:", error);
                return null;
            });
    }

    function deleteAluno(alunoId: number, force = false): Promise<DeleteGuardResult> {
        setLoading(true);
        return api
            .delete(`/Aluno/${alunoId}${force ? "?force=true" : ""}`)
            .then(() => {
                showToast("success", "Aluno deletado com sucesso.");
                setLoading(false);
                return { status: "ok" } as DeleteGuardResult;
            })
            .catch((error) => {
                setLoading(false);
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                    return { status: "error" } as DeleteGuardResult;
                }
                if (error?.response?.status === 409) {
                    return {
                        status: "conflict",
                        message: error.response.data?.message ?? "O aluno possui dependentes.",
                        dependentes: error.response.data?.dependentes ?? {},
                    } as DeleteGuardResult;
                }
                if (error?.response?.status === 404) {
                    return { status: "notFound" } as DeleteGuardResult;
                }
                showToast("error", "Erro ao deletar aluno. Verifique novamente mais tarde.", error);
                console.error("Erro ao deletar aluno:", error);
                return { status: "error" } as DeleteGuardResult;
            });
    }

    function handleGetAlunoByUserId(userId: number): Promise<AlunoType | null> {
        setLoading(true);
        return api
            .get(`/Aluno/getAlunoByUserId/${userId}`)
            .then((response) => {
                const aluno: AlunoType = response.data;
                setAluno(aluno);
                setLoading(false);
                return aluno;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao buscar informações do Aluno. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao buscar informações do Aluno:", error);
                return null;
            });
    }

    function handleGetAlunoById(alunoId: number): Promise<AlunoType | null> {
        setLoading(true);
        return api
            .get(`/Aluno/${alunoId}`)
            .then((response) => {
                const aluno: AlunoType = response.data;
                setAluno(aluno);
                setLoading(false);
                return aluno;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao buscar Aluno pelo ID. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao buscar Aluno pelo ID:", error);
                return null;
            });
    }

    function handleGetAlunoByReferenceCode(referenceCode: number): Promise<AlunoType | null> {
        setLoading(true);
        return api
            .get(`/Aluno/getAlunoByReferenceCode/${referenceCode}`)
            .then((response) => {
                const aluno: AlunoType = response.data;
                setAluno(aluno);
                setLoading(false);
                return aluno;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao buscar Aluno pelo código. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao buscar Aluno pelo código:", error);
                return null;
            });
    }

    function getAlunosDetalhadosPaged(
        pageNumber: number,
        empresaId: number,
        pageSize?: number,
        name?: string,
        alunoId?: number,
        contratoId?: number,
        relatorioId?: number,
        albumId?: number,
        cidade?: string,
        cursoId?: number
    ): Promise<AlunoDetalhadoType[] | null> {
        setLoading(true);
        let url = `/Aluno/getAlunosDetalhadosPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (empresaId) url += `&EmpresaId=${empresaId}`;
        if (name) url += `&Name=${name}`;
        if (alunoId) url += `&AlunoId=${alunoId}`;
        if (contratoId) url += `&ContratoId=${contratoId}`;
        if (relatorioId) url += `&RelatorioId=${relatorioId}`;
        if (albumId) url += `&AlbumId=${albumId}`;
        if (cidade) url += `&Cidade=${cidade}`;
        if (cursoId) url += `&CursoId=${cursoId}`;

        return api
            .get(url)
            .then((response) => {
                setListAlunoDetalhado(response.data.result);
                setAlunoDetalhadoPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    showToast("error", "Erro de autenticação. Atualize a página ou faça login novamente.");
                } else {
                    showToast("error", "Erro ao carregar alunos detalhados. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao carregar alunos detalhados:", error);
                return null;
            });
    }

    async function gerarRelatorioAlunos(request: AlunoRelatorioRequest): Promise<void> {
        setLoading(true);
        try {
            const response = await api.post("/Aluno/gerar-relatorio-alunos", request, { responseType: "blob" });
            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "relatorio-alunos.pdf");
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            showToast("success", "Relatório de alunos gerado com sucesso.");
        } catch (error: any) {
            let errorMsg = "Erro ao gerar relatório de alunos.";
            if (error?.response?.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    if (text) errorMsg = text;
                } catch {
                    // fallback
                }
            } else if (error?.response?.data) {
                errorMsg = String(error.response.data);
            }
            showToast("error", errorMsg);
            console.error("Erro ao gerar relatório de alunos:", error);
        } finally {
            setLoading(false);
        }
    }

    function validateReferenceCode(code: number): Promise<{ message: string; nome?: string; id?: number } | null> {
        return api
            .get(`/Aluno/validate-reference-code/${code}`)
            .then((response) => response.data)
            .catch((error) => {
                if (error?.response?.status === 409) return error.response.data;
                console.error("Erro ao validar código de referência:", error);
                return null;
            });
    }

    function copyTransferAlunos(payload: CopyTransferAlunosRequest): Promise<CopyTransferAlunosResponse | null> {
        setLoading(true);

        const safePayload: CopyTransferAlunosRequest = {
            sourceContratoId: Number(payload?.sourceContratoId ?? 0),
            targetContratoId: Number(payload?.targetContratoId ?? 0),
            alunos: (payload?.alunos ?? [])
                .map((x) => ({ alunoId: Number(x.alunoId), cursoId: Number(x.cursoId) }))
                .filter((x) => x.alunoId > 0 && x.cursoId > 0),
            targetCursoId: payload?.targetCursoId ? Number(payload.targetCursoId) : undefined,
            mode: (payload?.mode ?? "copy"),
        };

        return api
            .post(`/Aluno/copy-transfer`, safePayload)
            .then((response) => {
                const data: CopyTransferAlunosResponse = response.data;

                if (data?.missingCursos?.length) {
                    showToast(
                        "error",
                        `O contrato destino não possui os seguintes cursos: ${data.missingCursos.join(", ")}. Adicione-os antes de transferir.`
                    );
                    setLoading(false);
                    return data;
                }

                const msg =
                    safePayload.mode === "transfer"
                        ? `Transferência concluída. Movidos: ${data.transferred} | Já vinculados: ${data.skippedAlreadyLinked} | Não encontrados: ${data.notFound}`
                        : `Cópia concluída. Copiados: ${data.copied} | Já vinculados: ${data.skippedAlreadyLinked} | Não encontrados: ${data.notFound}`;

                showToast("success", msg);

                if (data?.errors?.length) {
                    showToast("warning", `Alguns itens tiveram erro (${data.errors.length}). Verifique os detalhes no console.`);
                    console.warn("copyTransferAlunos errors:", data.errors);
                }

                setLoading(false);
                return data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    const serverMsg = error?.response?.data?.message || error?.response?.data || null;
                    showToast("error", serverMsg ? String(serverMsg) : "Erro ao copiar/transferir alunos. Tente novamente.", error);
                }
                setLoading(false);
                console.error("Erro ao copiar/transferir alunos:", error);
                return null;
            });
    }

    function removeAlunoFromContrato(
        contratoId: number,
        alunoId: number,
        cursoId: number
    ): Promise<RemoveAlunoFromContratoResponse | null> {
        setLoading(true);

        const cId = Number(contratoId ?? 0);
        const aId = Number(alunoId ?? 0);
        const csId = Number(cursoId ?? 0);

        if (cId <= 0 || aId <= 0 || csId <= 0) {
            showToast("error", "ContratoId, AlunoId ou CursoId inválido.");
            setLoading(false);
            return Promise.resolve(null);
        }

        return api
            .delete(`/Aluno/contratos/${cId}/cursos/${csId}/alunos/${aId}`)
            .then((response) => {
                const data: RemoveAlunoFromContratoResponse = response.data;
                showToast("success", data?.message || "Vínculo do aluno com este curso removido com sucesso.");
                setLoading(false);
                return data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else if (error?.response?.status === 404) {
                    const msg =
                        error?.response?.data?.message ||
                        "Vínculo do aluno com este curso do contrato não encontrado.";
                    showToast("warning", String(msg));
                } else {
                    const serverMsg = error?.response?.data?.message || error?.response?.data || null;
                    showToast(
                        "error",
                        serverMsg ? String(serverMsg) : "Erro ao remover aluno do contrato. Tente novamente.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao remover aluno do contrato:", error);
                return null;
            });
    }

    function getAlunosSemVinculo(
        pageNumber: number,
        empresaId: number,
        pageSize?: number,
        name?: string,
        referenceCode?: number
    ): Promise<{ result: AlunoType[]; pageCount: number } | null> {
        setLoading(true);

        const pNumber = Number(pageNumber ?? 1);
        const pSize = Number(pageSize ?? 10);
        const eId = Number(empresaId ?? 0);

        if (eId <= 0) {
            showToast("error", "EmpresaId inválido.");
            setLoading(false);
            return Promise.resolve(null);
        }

        const params: any = {
            pageNumber: pNumber,
            pageSize: pSize,
            empresaId: eId,
        };

        if (name) {
            params.name = name;
        }

        if (referenceCode && referenceCode > 0) {
            params.referenceCode = referenceCode;
        }

        return api
            .get(`/Aluno/sem-vinculo`, { params })
            .then((response) => {
                const data = response.data;
                setLoading(false);
                return data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    const serverMsg = error?.response?.data?.message || error?.response?.data || null;
                    showToast(
                        "error",
                        serverMsg ? String(serverMsg) : "Erro ao buscar alunos sem vínculo. Tente novamente.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar alunos sem vínculo:", error);
                return null;
            });
    }

    function revinculaAluno(
        alunoId: number,
        contratoId: number,
        cursoId: number
    ): Promise<boolean> {
        setLoading(true);

        const aId = Number(alunoId ?? 0);
        const cId = Number(contratoId ?? 0);
        const csId = Number(cursoId ?? 0);

        if (aId <= 0 || cId <= 0 || csId <= 0) {
            showToast("error", "AlunoId, ContratoId ou CursoId inválido.");
            setLoading(false);
            return Promise.resolve(false);
        }

        const payload = {
            contratoId: cId,
            cursoId: csId,
        };

        return api
            .post(`/Aluno/${aId}/revincular`, payload)
            .then((response) => {
                showToast("success", "Aluno revinculado com sucesso.");
                setLoading(false);
                return true;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else if (error?.response?.status === 409) {
                    showToast("warning", "Aluno já está vinculado a este contrato/curso.");
                } else if (error?.response?.status === 404) {
                    showToast("error", "Vínculo entre contrato e curso não encontrado.");
                } else {
                    const serverMsg = error?.response?.data?.message || error?.response?.data || null;
                    showToast(
                        "error",
                        serverMsg ? String(serverMsg) : "Erro ao revincular aluno. Tente novamente.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao revincular aluno:", error);
                return false;
            });
    }

    function alterarCursoAluno(alunoId: number, contratoId: number, novoCursoId: number): Promise<boolean> {
        return api
            .put(`/Aluno/${alunoId}/curso`, { contratoId, novoCursoId })
            .then(() => {
                showToast("success", "Curso do aluno alterado com sucesso.");
                return true;
            })
            .catch((error) => {
                const serverMsg = error?.response?.data || null;
                showToast("error", serverMsg ? String(serverMsg) : "Erro ao alterar curso do aluno.", error);
                return false;
            });
    }

    async function handleGetVendaByAlunoId(alunoId: number): Promise<VendaDetalhadaAlunoType | null> {
        try {
            const response = await api.get<VendaDetalhadaAlunoType>(`/Aluno/getVendaByAlunoId/${alunoId}`);
            return response.data;
        } catch (error) {
            console.error("Erro ao buscar detalhes da venda do aluno:", error);
            return null;
        }
    }

    return {
        createAluno,
        getAlunoPaged,
        updateAluno,
        deleteAluno,
        handleGetAlunoByUserId,
        handleGetAlunoById,
        handleGetAlunoByReferenceCode,
        handleGetVendaByAlunoId,

        // ✅ NOVO
        copyTransferAlunos,
        removeAlunoFromContrato,
        getAlunosSemVinculo,
        revinculaAluno,
        alterarCursoAluno,

        setListAluno,
        listAluno,
        setAlunoPageCount,
        alunoPageCount,
        totalAlunos,
        setTotalAlunos,
        setInitialListAluno,
        initialListAluno,
        setAluno,
        aluno,
        getAlunosDetalhadosPaged,
        listAlunoDetalhado,
        alunoDetalhadoPageCount,
        gerarRelatorioAlunos,
        validateReferenceCode,
    };
}